// v2/pages/QuotesV2.js — Teklif Al: sablon indir, Excel yukle, Tekliflerim
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  FileSpreadsheet, Upload, Download, FileText, X, Loader2, Inbox, ChevronLeft,
  ChevronRight, RotateCw, Info, Eye, Clock, CheckCircle,
} from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import ToastStack from '../components/Toast';
import useToast from '../hooks/useToast';
import useMenuNav from '../hooks/useMenuNav';
import useCartContext from '../hooks/CartContext';
import useExchangeRates from '../hooks/useExchangeRates';
import {
  useQuoteList, quoteApi, QUOTE_STATUS, fmtTarih, kalanGun, karsilamaOrani, fmtEur,
} from '../hooks/useQuotes';
import { formatTRY, formatNumber } from '../utils/format';
import styles from '../styles/DashboardV2.module.css';
import css from '../styles/QuotesV2.module.css';
import '../styles/tokens.css';

const MAX_MB = 5;
const UZANTILAR = ['.xlsx', '.xlsm', '.csv'];
const ASAMALAR = [
  'Dosya yükleniyor…',
  'Ürün kodları eşleştiriliyor…',
  'Carinize özel fiyatlar hesaplanıyor…',
  'Stok durumu kontrol ediliyor…',
  'Teklif hazırlanıyor…',
];
const PER = 10;

function boyut(b) {
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${formatNumber(b / 1024, 0)} KB`;
  return `${formatNumber(b / 1024 / 1024, 1)} MB`;
}

// ---------------------------------------------------------------- yukleme
function Yukleme({ onBasari, toast }) {
  const [dosya, setDosya] = useState(null);
  const [not, setNot] = useState('');
  const [surukle, setSurukle] = useState(false);
  const [mesgul, setMesgul] = useState(false);
  const [asama, setAsama] = useState(0);
  const [hata, setHata] = useState(null);
  const [sablonMesgul, setSablonMesgul] = useState(false);
  const input = useRef(null);
  const zamanlayici = useRef(null);

  useEffect(() => () => clearInterval(zamanlayici.current), []);

  const sec = useCallback((f) => {
    setHata(null);
    if (!f) return;
    const ad = (f.name || '').toLowerCase();
    if (!UZANTILAR.some((u) => ad.endsWith(u))) {
      setHata(ad.endsWith('.xls')
        ? 'Eski Excel biçimi (.xls) desteklenmiyor. Dosyayı Excel\'de "Farklı Kaydet → Excel Çalışma Kitabı (.xlsx)" ile kaydedin.'
        : 'Lütfen .xlsx veya .csv dosyası seçin.');
      return;
    }
    if (f.size > MAX_MB * 1024 * 1024) {
      setHata(`Dosya boyutu en fazla ${MAX_MB} MB olabilir.`);
      return;
    }
    setDosya(f);
  }, []);

  const sablon = useCallback(async () => {
    setSablonMesgul(true);
    try {
      await quoteApi.template();
    } catch (e) {
      toast.push(e.message || 'Şablon indirilemedi', 'error');
    } finally {
      setSablonMesgul(false);
    }
  }, [toast]);

  const gonder = useCallback(async () => {
    if (!dosya || mesgul) return;
    setMesgul(true);
    setHata(null);
    setAsama(0);
    zamanlayici.current = setInterval(
      () => setAsama((a) => Math.min(a + 1, ASAMALAR.length - 1)), 1400);
    try {
      const v = await quoteApi.create(dosya, not.trim());
      onBasari(v.quote);
    } catch (e) {
      setHata(e.message || 'Teklif oluşturulamadı');
      setMesgul(false);
    } finally {
      clearInterval(zamanlayici.current);
    }
  }, [dosya, not, mesgul, onBasari]);

  return (
    <section className={css.card}>
      <div className={css.cardHead}>
        <span className={css.cardBar} />
        <h2 className={css.cardTitle}>Yeni Teklif</h2>
      </div>

      <ol className={css.steps}>
        <li className={css.step}>
          <span className={css.stepNo}>1</span>
          <span className={css.stepBody}>
            <span className={css.stepTitle}>Şablonu indirin</span>
            <span className={css.stepText}>Ürün kodu ve miktar sütunlarını içeren hazır Excel şablonu.</span>
            <button type="button" className={css.btnGhost} onClick={sablon} disabled={sablonMesgul}>
              {sablonMesgul ? <Loader2 size={14} className={css.spin} /> : <Download size={14} />}
              Excel Şablonunu İndir
            </button>
          </span>
        </li>
        <li className={css.step}>
          <span className={css.stepNo}>2</span>
          <span className={css.stepBody}>
            <span className={css.stepTitle}>Ürünleri yazın</span>
            <span className={css.stepText}>
              Bosch ürün kodu, eski Bosch kodu veya OEM numarası yazabilirsiniz.
              Her satıra bir ürün; en fazla 35.000 satır.
            </span>
          </span>
        </li>
        <li className={css.step}>
          <span className={css.stepNo}>3</span>
          <span className={css.stepBody}>
            <span className={css.stepTitle}>Dosyayı yükleyin</span>
            <span className={css.stepText}>
              Carinize özel indirimler ve güncel stok durumuyla teklifiniz hazırlanır.
            </span>
          </span>
        </li>
      </ol>

      <div
        className={`${css.drop} ${surukle ? css.dropOn : ''} ${dosya ? css.dropFull : ''}`}
        onDragOver={(e) => { e.preventDefault(); if (!mesgul) setSurukle(true); }}
        onDragLeave={() => setSurukle(false)}
        onDrop={(e) => {
          e.preventDefault();
          setSurukle(false);
          if (!mesgul) sec(e.dataTransfer.files && e.dataTransfer.files[0]);
        }}
        onClick={() => { if (!mesgul && !dosya && input.current) input.current.click(); }}
        role="button" tabIndex={0}
        onKeyDown={(e) => { if ((e.key === 'Enter' || e.key === ' ') && !dosya && input.current) input.current.click(); }}
        aria-label="Excel dosyası seçin veya sürükleyip bırakın"
      >
        <input
          ref={input} type="file" accept={UZANTILAR.join(',')} hidden
          onChange={(e) => { sec(e.target.files && e.target.files[0]); e.target.value = ''; }}
        />
        {mesgul ? (
          <div className={css.dropBusy}>
            <Loader2 size={30} className={css.spin} />
            <span className={css.dropTitle}>{ASAMALAR[asama]}</span>
            <span className={css.dropHint}>Büyük dosyalarda bu işlem birkaç saniye sürebilir.</span>
            <span className={css.progress}><span className={css.progressIndet} /></span>
          </div>
        ) : dosya ? (
          <div className={css.fileRow}>
            <span className={css.fileIcon}><FileSpreadsheet size={26} /></span>
            <span className={css.fileBody}>
              <span className={css.fileName}>{dosya.name}</span>
              <span className={css.fileMeta}>{boyut(dosya.size)}</span>
            </span>
            <button type="button" className={css.iconBtn} aria-label="Dosyayı kaldır"
                    onClick={(e) => { e.stopPropagation(); setDosya(null); }}>
              <X size={16} />
            </button>
          </div>
        ) : (
          <>
            <span className={css.dropIcon}><Upload size={28} /></span>
            <span className={css.dropTitle}>Excel dosyanızı buraya sürükleyin</span>
            <span className={css.dropHint}>veya <u>bilgisayarınızdan seçin</u> · .xlsx, .csv · en fazla {MAX_MB} MB</span>
          </>
        )}
      </div>

      {hata && <div className={css.alertRed} role="alert"><Info size={14} />{hata}</div>}

      <label className={css.noteLabel}>
        Teklif notu <span className={css.optional}>(isteğe bağlı)</span>
        <textarea
          className={css.note} rows={2} maxLength={500} value={not} disabled={mesgul}
          onChange={(e) => setNot(e.target.value)}
          placeholder="Örn. Kasım ayı stok siparişi"
        />
      </label>

      <div className={css.actions}>
        <span className={css.hint}>
          <Clock size={13} /> Teklif 7 gün geçerlidir. Sipariş anında güncel fiyat uygulanır.
        </span>
        <button type="button" className={css.btnPrimary} disabled={!dosya || mesgul} onClick={gonder}>
          {mesgul ? <Loader2 size={15} className={css.spin} /> : <FileText size={15} />}
          Teklif Oluştur
        </button>
      </div>
    </section>
  );
}

// ---------------------------------------------------------------- liste
function DurumRozet({ q }) {
  const st = QUOTE_STATUS[q.status] || QUOTE_STATUS['Hazır'];
  return <span className={`${css.pill} ${css[st.tone]}`}>{st.label}</span>;
}

function Gecerlilik({ q }) {
  const gun = kalanGun(q.validUntil);
  if (q.expired || gun === null || gun < 0) {
    return <span className={css.muted}>{fmtTarih(q.validUntil, false)}</span>;
  }
  return (
    <span className={css.validCell}>
      {fmtTarih(q.validUntil, false)}
      <span className={`${css.daysLeft} ${gun <= 1 ? css.daysWarn : ''}`}>
        {gun === 0 ? 'bugün bitiyor' : `${gun} gün kaldı`}
      </span>
    </span>
  );
}

function Tekliflerim({ yeniNo }) {
  const [page, setPage] = useState(1);
  const liste = useQuoteList(page, PER);

  return (
    <section className={css.card}>
      <div className={css.cardHead}>
        <span className={css.cardBar} />
        <h2 className={css.cardTitle}>Tekliflerim</h2>
        <span className={css.cardChip}>{formatNumber(liste.totalItems)} teklif</span>
        <span className={css.spacer} />
        <button type="button" className={css.iconBtn} onClick={liste.yenile} aria-label="Yenile">
          <RotateCw size={14} />
        </button>
      </div>

      {liste.error && <div className={css.alertRed}>{liste.error}</div>}

      {liste.loading ? (
        <div className={css.skelList}>{[0, 1, 2].map((i) => <div key={i} className={css.skel} />)}</div>
      ) : liste.items.length === 0 ? (
        <div className={css.empty}>
          <Inbox size={32} strokeWidth={1.3} />
          <span>Henüz teklifiniz yok. Yukarıdan ilk teklifinizi oluşturabilirsiniz.</span>
        </div>
      ) : (
        <div className={css.tableScroll}>
          <table className={css.table}>
            <thead>
              <tr>
                <th>Teklif No</th>
                <th>Tarih</th>
                <th>Geçerlilik</th>
                <th className={css.right}>Kalem</th>
                <th>Karşılama</th>
                <th className={css.right}>Net Tutar</th>
                <th>Durum</th>
                <th aria-label="İşlem" />
              </tr>
            </thead>
            <tbody>
              {liste.items.map((q) => {
                const oran = karsilamaOrani(q);
                return (
                  <tr key={q.no} className={q.no === yeniNo ? css.rowNew : undefined}>
                    <td className={css.cMain}>
                      <Link to={`/v2/quotes/${q.no}`} className={css.quoteNo}>{q.no}</Link>
                      {q.fileName && <span className={css.subText}>{q.fileName}</span>}
                    </td>
                    <td data-label="Tarih">{fmtTarih(q.createdAt)}</td>
                    <td data-label="Geçerlilik"><Gecerlilik q={q} /></td>
                    <td className={css.right} data-label="Kalem">{formatNumber(q.lineCount)}</td>
                    <td data-label="Karşılama">
                      <span className={css.rateCell}>
                        <span className={css.rateTrack}>
                          <span className={css.rateFill} style={{ width: `${oran}%` }} />
                        </span>
                        <span className={css.rateText}>%{formatNumber(oran, 0)}</span>
                      </span>
                    </td>
                    <td className={`${css.right} ${css.money}`} data-label="Net Tutar">
                      <span className={css.priceCell}>
                        <span>{formatTRY(q.totals.net)}</span>
                        {q.totalsEur && <span className={css.eur}>{fmtEur(q.totalsEur.net)}</span>}
                      </span>
                    </td>
                    <td data-label="Durum"><DurumRozet q={q} /></td>
                    <td className={`${css.right} ${css.cAct}`}>
                      <Link to={`/v2/quotes/${q.no}`} className={css.btnSmall}>
                        <Eye size={13} /> Görüntüle
                      </Link>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {liste.totalPages > 1 && (
        <div className={css.pager}>
          <button type="button" className={css.pageBtn} disabled={page <= 1}
                  onClick={() => setPage((p) => p - 1)} aria-label="Önceki sayfa">
            <ChevronLeft size={15} />
          </button>
          <span className={css.pagerInfo}>Sayfa {page}/{liste.totalPages}</span>
          <button type="button" className={css.pageBtn} disabled={page >= liste.totalPages}
                  onClick={() => setPage((p) => p + 1)} aria-label="Sonraki sayfa">
            <ChevronRight size={15} />
          </button>
        </div>
      )}
    </section>
  );
}

// ---------------------------------------------------------------- sayfa
export default function QuotesV2() {
  const navigate = useNavigate();
  const [headerTerm, setHeaderTerm] = useState('');
  const cart = useCartContext();
  const toast = useToast();
  const kurlar = useExchangeRates();
  const handleMenu = useMenuNav(toast.push);

  const basari = useCallback((q) => {
    toast.push(`${q.no} numaralı teklifiniz hazır`, 'success');
    navigate(`/v2/quotes/${q.no}`, { state: { yeni: true } });
  }, [navigate, toast]);

  return (
    <div className={styles.page}>
      <ToastStack items={toast.items} onDismiss={toast.dismiss} />
      <Header
        searchTerm={headerTerm}
        onSearchChange={setHeaderTerm}
        cart={cart}
        eurRate={kurlar.eurRate}
        onMenuSelect={handleMenu}
      />
      <main className={styles.main}>
        <div className={css.head}>
          <h1 className={css.title}>
            Teklif Al
            <span className={css.titleSub}>
              Excel listenizi yükleyin; carinize özel fiyat ve stok durumuyla profesyonel teklifiniz hazırlansın
            </span>
          </h1>
          <span className={css.headBadges}>
            <span className={css.headBadge}><CheckCircle size={13} /> Carinize özel indirimler</span>
            <span className={css.headBadge}><CheckCircle size={13} /> PDF ve Excel çıktı</span>
            <span className={css.headBadge}><CheckCircle size={13} /> Tek tıkla sepete aktarım</span>
          </span>
        </div>

        <Yukleme onBasari={basari} toast={toast} />
        <Tekliflerim />
      </main>
      <Footer />
    </div>
  );
}
